import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Typography,
  Paper,
  Toolbar,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem as SelectMenuItem,
  Autocomplete,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Footer from "../../../examples/Footer";
import { hasPermissionDirect } from "../../../utils/hasPermissions";
import ActionsMenu from "./ActionsMenu";
import axios from "axios";
import PropTypes from "prop-types";

export default function InternalStaff() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // State variables
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const [designations, setDesignations] = useState([]);
  const [qualifications, setQualifications] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [departmentsError, setDepartmentsError] = useState("");
  const [departmentDialogOpen, setDepartmentDialogOpen] = useState(false);
  const [departmentForm, setDepartmentForm] = useState({
    department_code: "",
    department_name: "",
  });
  const [departmentSaving, setDepartmentSaving] = useState(false);
  const [departmentSaveError, setDepartmentSaveError] = useState("");
  const [designationsLoading, setDesignationsLoading] = useState(false);
  const [qualificationsLoading, setQualificationsLoading] = useState(false);
  const [courseCodes, setCourseCodes] = useState([]);
  const [courseCodesLoading, setCourseCodesLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  // Form state
  const [staffFormData, setStaffFormData] = useState({
    staff_code: "",
    salutation: "",
    staff_name: "",
    first_name: "",
    middle_name: "",
    last_name: "",
    father_name: "",
    email: "",
    aadhar_no: "",
    pan_no: "",
    dob: "",
    doj: "",
    dor: "",
    gender: "",
    designation_id: "",
    qualification_id: "",
    department_id: "",
    staff_type: "",
    mobile: "",
    experience_in_college: "",
    experience_in_outer_colleges: "",
    course_code: "",
    staff_status: "Active",
    workflow_status: "DRAFT",
  });
  const [staffFormErrors, setStaffFormErrors] = useState({});

  // Get user data from session storage (memoized to prevent infinite loops)
  const user = useMemo(() => {
    const userData = sessionStorage.getItem("user");
    const parsedUser = userData ? JSON.parse(userData) : null;

    return parsedUser;
  }, []);

  // Helper functions
  const getAuthHeaders = () => {
    const token = sessionStorage.getItem("token");

    return {
      Authorization: `Bearer ${token}`,
    };
  };

  const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email);
  const isValidAadhar = (aadhar) => /^\d{12}$/.test(aadhar);
  const isValidMobile = (mobile) => /^\d{10}$/.test(mobile);
  const isValidPan = (pan) => /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan);

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Status cell component with deactivate functionality
  const ActiveStatusCell = ({ value, row }) => {
    const handleStatusToggle = async () => {
      const currentStatus = value === "Active" || value === "ACTIVE" ? "Active" : "Inactive";
      const newStatus = currentStatus === "Active" ? "Inactive" : "Active";

      try {
        let response;
        if (currentStatus === "Active") {
          // Use deactivate endpoint for better tracking
          response = await axios.patch(
            `${API_BASE_URL}/api/internal-staff-management/${user.client_id}/${user.college_id}/${row.staff_id}/deactivate`,
            { user_id: user?.user_id || "system" },
            { headers: getAuthHeaders() }
          );
        } else {
          // Reactivate using regular update
          response = await axios.put(
            `${API_BASE_URL}/api/internal-staff-management/${user.client_id}/${user.college_id}/${row.staff_id}`,
            {
              staff_status: "Active",
              modified_by: user?.user_email || "unknown",
              user_id: user?.user_id || "system",
            },
            { headers: getAuthHeaders() }
          );
        }

        if (response.status === 200) {
          showSnackbar(response.data.message || `Status updated to ${newStatus}`, "success");
          fetchData();
        }
      } catch (error) {
        const errorMessage = error.response?.data?.message || "Error updating status";
        showSnackbar(errorMessage, "error");
        console.error("Status update error:", error);
      }
    };

    return (
      <Chip
        label={value === "Active" || value === "ACTIVE" ? "Active" : "Inactive"}
        color={value === "Active" || value === "ACTIVE" ? "success" : "error"}
        size="medium"
        variant="outlined"
        onClick={handleStatusToggle}
        sx={{
          cursor: "pointer",
          "&:hover": {
            opacity: 0.8,
            transform: "scale(1.05)",
          },
        }}
        title={
          value === "Active" || value === "ACTIVE"
            ? "Click to deactivate (staff cannot be allotted to scripts)"
            : "Click to activate staff"
        }
      />
    );
  };

  ActiveStatusCell.propTypes = {
    value: PropTypes.string.isRequired,
    row: PropTypes.object.isRequired,
  };

  // Fetch data (memoized to prevent unnecessary re-renders)
  const fetchData = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    try {
      const url = `${API_BASE_URL}/api/internal-staff-management/${user.client_id}/${user.college_id}`;
      const response = await axios.get(url, { headers: getAuthHeaders() });

      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        data = response.data.data;
      }

      const formattedData = data.map((item, index) => ({
        sno: index + 1,
        id: item.staff_id,
        course_code: item.course_code || "", // Handle missing course_code gracefully
        ...item,
      }));

      setAllRows(formattedData);
      setRows(formattedData);
    } catch (error) {
      showSnackbar("Internal staff data not found", "error");
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch designations for dropdown
  const fetchDesignations = useCallback(async () => {
    if (!user) {
      return;
    }

    // Use fallback values if user context is missing required fields
    const clientId = user.client_id || 1;
    const collegeId = user.college_id || 1;

    setDesignationsLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/staff-designations/${clientId}/${collegeId}`,
        { headers: getAuthHeaders() }
      );

      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        setDesignations(response.data.data);
      } else {
        setDesignations([]);
      }
    } catch (error) {
      console.error("Error fetching designations:", error);
      console.error("Error response:", error.response?.data);
      setDesignations([]);
    } finally {
      setDesignationsLoading(false);
    }
  }, [user]);

  // Fetch qualifications for dropdown
  const fetchQualifications = useCallback(async () => {
    if (!user) {
      return;
    }

    // Use fallback values if user context is missing required fields
    const clientId = user.client_id || 1;
    const collegeId = user.college_id || 1;

    setQualificationsLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/staff-qualifications/${clientId}/${collegeId}`,
        { headers: getAuthHeaders() }
      );

      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        setQualifications(response.data.data);
      } else {
        setQualifications([]);
      }
    } catch (error) {
      setQualifications([]);
    } finally {
      setQualificationsLoading(false);
    }
  }, [user]);

  // Fetch course codes for dropdown
  const handleAddDepartment = async () => {
    if (departmentSaving) return;
    if (!departmentForm.department_code.trim() || !departmentForm.department_name.trim()) {
      setDepartmentSaveError("Enter department code and name");
      return;
    }
    setDepartmentSaving(true);
    setDepartmentSaveError("");
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/internal-staff-management/${user.client_id}/${user.college_id}/departments`,
        departmentForm,
        { headers: getAuthHeaders() }
      );
      const department = response.data.data;
      setDepartments((current) =>
        [...current, department].sort((a, b) => a.department_name.localeCompare(b.department_name))
      );
      setStaffFormData((current) => ({ ...current, department_id: department.department_id }));
      setDepartmentDialogOpen(false);
      showSnackbar("Department added successfully", "success");
    } catch (error) {
      setDepartmentSaveError(error.response?.data?.message || "Unable to add department");
    } finally {
      setDepartmentSaving(false);
    }
  };

  const fetchDepartments = useCallback(async () => {
    if (!user?.client_id || !user?.college_id) return;
    setDepartmentsLoading(true);
    setDepartmentsError("");
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/internal-staff-management/${user.client_id}/${user.college_id}/departments`,
        { headers: getAuthHeaders() }
      );
      setDepartments(response.data.data || []);
    } catch (error) {
      setDepartments([]);
      setDepartmentsError("Unable to load departments. Close and reopen the form to retry.");
    } finally {
      setDepartmentsLoading(false);
    }
  }, [user, API_BASE_URL]);

  useEffect(() => {
    if (open) fetchDepartments();
  }, [open, fetchDepartments]);

  const fetchCourseCodes = useCallback(async () => {
    if (!user) return;

    const clientId = user.client_id || 1;
    const collegeId = user.college_id || 1;

    setCourseCodesLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/course-codes/${clientId}/${collegeId}`,
        { headers: getAuthHeaders() }
      );

      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        setCourseCodes(response.data.data);
      } else {
        setCourseCodes([]);
      }
    } catch (error) {
      setCourseCodes([]);
    } finally {
      setCourseCodesLoading(false);
    }
  }, [user]);

  // Menu handlers
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Form handlers
  const handleOpenAdd = () => {
    setEditing(null);
    setStaffFormData({
      staff_code: "",
      salutation: "",
      staff_name: "",
      first_name: "",
      middle_name: "",
      last_name: "",
      father_name: "",
      email: "",
      aadhar_no: "",
      pan_no: "",
      dob: "",
      doj: "",
      dor: "",
      gender: "",
      designation_id: "",
      qualification_id: "",
      department_id: "",
      staff_type: "",
      mobile: "",
      experience_in_college: "",
      experience_in_outer_colleges: "",
      course_code: "",
      staff_status: "Active",
      workflow_status: "DRAFT",
    });
    setStaffFormErrors({});
    setOpen(true);
  };

  const handleOpenEdit = (row) => {
    setEditing(row);
    setStaffFormData({
      staff_code: row.staff_code || "",
      salutation: row.salutation || "",
      staff_name: row.staff_name || "",
      first_name: row.first_name || "",
      middle_name: row.middle_name || "",
      last_name: row.last_name || "",
      father_name: row.father_name || "",
      email: row.email || "",
      aadhar_no: row.aadhar_no || "",
      pan_no: row.pan_no || "",
      dob: row.dob ? row.dob.split("T")[0] : "",
      doj: row.doj ? row.doj.split("T")[0] : "",
      dor: row.dor ? row.dor.split("T")[0] : "",
      gender: row.gender || "",
      designation_id: row.designation_id || "",
      qualification_id: row.qualification_id || "",
      department_id: row.department_id || "",
      staff_type: row.staff_type || "",
      mobile: row.mobile || "",
      experience_in_college: row.experience_in_college ?? "",
      experience_in_outer_colleges: row.experience_in_outer_colleges ?? "",
      course_code: row.course_code || "",
      staff_status: row.staff_status || "Active",
      workflow_status: row.workflow_status || "DRAFT",
    });
    setStaffFormErrors({});
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this staff member?")) return;

    try {
      const response = await axios.delete(
        `${API_BASE_URL}/api/internal-staff-management/${user.client_id}/${user.college_id}/${id}`,
        { headers: getAuthHeaders() }
      );

      if (response.status === 200) {
        showSnackbar("Staff deleted successfully", "success");
        fetchData();
      }
    } catch (error) {
      showSnackbar("Error deleting staff", "error");
    }
  };

  const handleSubmit = async () => {
    const errors = {};

    // MANDATORY VALIDATION RULES
    if (!staffFormData.staff_name?.trim()) {
      errors.staff_name = "Staff Name is mandatory";
    } else if (staffFormData.staff_name.length > 100) {
      errors.staff_name = "Staff Name cannot exceed 100 characters";
    }

    if (!staffFormData.email?.trim()) {
      errors.email = "Email is required";
    } else if (!isValidEmail(staffFormData.email)) {
      errors.email = "Please enter a valid email format (example@domain.com)";
    }

    // Strict validation for Aadhar, PAN, Mobile if provided
    if (staffFormData.aadhar_no?.trim()) {
      if (!isValidAadhar(staffFormData.aadhar_no)) {
        errors.aadhar_no = "Aadhar Number must be exactly 12 digits";
      }
    }

    if (staffFormData.pan_no?.trim()) {
      if (!isValidPan(staffFormData.pan_no)) {
        errors.pan_no = "PAN Number must be in format ABCDE1234F (5 letters, 4 digits, 1 letter)";
      }
    }

    if (staffFormData.mobile?.trim()) {
      if (!isValidMobile(staffFormData.mobile)) {
        errors.mobile = "Mobile Number must be exactly 10 digits";
      }
    }

    // Additional validation for first_name
    if (!staffFormData.first_name?.trim() && staffFormData.staff_name?.trim()) {
      // Auto-extract first name from staff name if not provided
      const nameParts = staffFormData.staff_name.trim().split(" ");
      staffFormData.first_name = nameParts[0] || staffFormData.staff_name.trim();
    }

    setStaffFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      showSnackbar("Please fix the validation errors before submitting", "error");
      return;
    }

    const userEmail = user?.user_email || "unknown";
    const userId = user?.user_id || "system";

    const workflowStatusMap = {
      Approved: "APPROVED",
      Draft: "DRAFT",
    };

    // Transform form data for API
    const formDataForAPI = {
      ...staffFormData,
      staff_status: staffFormData.staff_status === "Active" ? "Active" : "Inactive",
      workflow_status: workflowStatusMap[staffFormData.workflow_status] || "INACTIVE",
      // Convert empty strings to null for optional fields, and ensure valid date format
      dob: staffFormData.dob && staffFormData.dob.trim() !== "" ? staffFormData.dob : null,
      doj: staffFormData.doj && staffFormData.doj.trim() !== "" ? staffFormData.doj : null,
      dor: staffFormData.dor && staffFormData.dor.trim() !== "" ? staffFormData.dor : null,
      designation_id: staffFormData.designation_id || null,
      qualification_id: staffFormData.qualification_id || null,
      department_id: staffFormData.department_id || null,
      experience_in_college:
        staffFormData.experience_in_college === ""
          ? null
          : Number(staffFormData.experience_in_college),
      experience_in_outer_colleges:
        staffFormData.experience_in_outer_colleges === ""
          ? null
          : Number(staffFormData.experience_in_outer_colleges),
      user_id: userId,
    };

    try {
      let response;
      if (editing) {
        const updateData = {
          ...formDataForAPI,
          modified_by: userEmail,
          user_id: userId,
        };

        response = await axios.put(
          `${API_BASE_URL}/api/internal-staff-management/${user.client_id}/${user.college_id}/${editing.staff_id}`,
          updateData,
          { headers: getAuthHeaders() }
        );
      } else {
        const createData = {
          ...formDataForAPI,
          created_by: userEmail,
          user_id: userId,
        };

        response = await axios.post(
          `${API_BASE_URL}/api/internal-staff-management/${user.client_id}/${user.college_id}`,
          createData,
          { headers: getAuthHeaders() }
        );
      }
      if (response.status === 200 || response.status === 201) {
        showSnackbar(response.data.message || "Saved successfully", "success");
      } else {
        showSnackbar("Something went wrong. Please try again.", "error");
      }
      setOpen(false);
      fetchData();
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Error saving Internal Staff";
      showSnackbar(errorMessage, "error");
      console.error("Save error details:", error.response?.data);
    }
  };

  // CSV Download function
  const handleDownloadCsv = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/internal-staff-management/${user.client_id}/${user.college_id}/download/csv`,
        {
          headers: getAuthHeaders(),
          responseType: "blob", // Important for file download
        }
      );

      // Create blob URL and download
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "internal_staff_management.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSnackbar("CSV downloaded successfully", "success");
    } catch (error) {
      console.error("Error downloading CSV:", error);
      // Fallback: Generate CSV from current data
      generateCsvFromData();
    }
  };

  // Fallback function to generate CSV from current data
  const generateCsvFromData = () => {
    try {
      const csvHeaders = [
        "ID",
        "Staff Code",
        "Salutation",
        "Staff Name",
        "First Name",
        "Middle Name",
        "Last Name",
        "Father Name",
        "Email",
        "Aadhar Number",
        "PAN Number",
        "Mobile",
        "Gender",
        "DOB",
        "DOJ",
        "DOR",
        "Designation",
        "Qualification",
        "Staff Type",
        "Staff Status",
        "Workflow Status",
      ];

      const csvData = allRows.map((row) => [
        row.staff_id || "",
        row.staff_code || "",
        row.salutation || "",
        row.staff_name || "",
        row.first_name || "",
        row.middle_name || "",
        row.last_name || "",
        row.father_name || "",
        row.email || "",
        row.aadhar_no || "",
        row.pan_no || "",
        row.mobile || "",
        row.gender || "",
        row.dob || "",
        row.doj || "",
        row.dor || "",
        row.designation?.designation_name || "",
        row.qualification?.qualification_name || "",
        row.staff_type || "",
        row.staff_status || "",
        row.workflow_status || "",
      ]);

      // Create CSV content
      const csvContent = [
        csvHeaders.join(","),
        ...csvData.map((row) => row.map((field) => `"${field}"`).join(",")),
      ].join("\n");

      // Create blob and download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "internal_staff_management.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSnackbar("CSV downloaded successfully", "success");
    } catch (error) {
      console.error("Error generating CSV:", error);
      showSnackbar("Error downloading CSV", "error");
    }
  };
  const handleUploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/api/internal-staff-management/${user.client_id}/${user.college_id}/upload/csv`,
        formData,
        { headers: getAuthHeaders() }
      );
      showSnackbar(data.message || "CSV uploaded successfully", "success");
      fetchData();
    } catch (err) {
      console.error(err);
      showSnackbar(err?.response?.data?.message || "Upload failed", "error");
    } finally {
      e.target.value = "";
    }
  };

  // PDF Download function
  const handleDownloadPdf = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/internal-staff-management/${user.client_id}/${user.college_id}/download/pdf`,
        {
          headers: getAuthHeaders(),
          responseType: "blob", // Important for file download
        }
      );

      // Create blob URL and download
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "internal_staff_management.pdf");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSnackbar("PDF downloaded successfully", "success");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      showSnackbar("Error downloading PDF. Please try again.", "error");
    }
  };

  // Effects
  useEffect(() => {
    if (user) {
      fetchData();
      fetchDesignations();
      fetchQualifications();
      fetchCourseCodes();
    }
  }, [user, fetchData, fetchDesignations, fetchQualifications, fetchCourseCodes]);

  useEffect(() => {
    if (open && user) {
      fetchDesignations();
      fetchQualifications();
      fetchCourseCodes();
    }
  }, [open, user, fetchDesignations, fetchQualifications, fetchCourseCodes]);

  // Local search filter
  useEffect(() => {
    if (!search) setRows(allRows);
    else {
      const s = search.toLowerCase();
      setRows(
        allRows.filter(
          (r) =>
            r.staff_name?.toLowerCase().includes(s) ||
            r.email?.toLowerCase().includes(s) ||
            r.staff_code?.toLowerCase().includes(s) ||
            r.mobile?.toLowerCase().includes(s) ||
            r.first_name?.toLowerCase().includes(s) ||
            r.last_name?.toLowerCase().includes(s) ||
            (r.course_code && r.course_code.toLowerCase().includes(s))
        )
      );
    }
  }, [search, allRows]);

  // Define columns for DataGrid
  const columns = useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No",
        width: 80,
      },
      { field: "staff_code", headerName: "Staff Code", flex: 1, minWidth: 100 },
      { field: "salutation", headerName: "Salutation", flex: 0.8, minWidth: 80 },
      { field: "staff_name", headerName: "Staff Name", flex: 1.5, minWidth: 150 },
      {
        field: "staff_status",
        headerName: "Staff Status",
        flex: 1,
        minWidth: 120,
        renderCell: (params) => <ActiveStatusCell value={params.value} row={params.row} />,
      },
      { field: "first_name", headerName: "First Name", flex: 1.2, minWidth: 120 },
      { field: "last_name", headerName: "Last Name", flex: 1.2, minWidth: 120 },
      { field: "email", headerName: "Email", flex: 2, minWidth: 200 },
      { field: "aadhar_no", headerName: "Aadhar Number", flex: 1.2, minWidth: 120 },
      { field: "pan_no", headerName: "PAN Number", flex: 1.2, minWidth: 120 },
      { field: "mobile", headerName: "Mobile", flex: 1.2, minWidth: 120 },
      {
        field: "experience_in_college",
        headerName: "Exp. in College",
        flex: 1.2,
        minWidth: 120,
        valueFormatter: ({ value }) =>
          value === null || value === undefined || typeof value === "object" ? "" : value,
      },
      {
        field: "experience_in_outer_colleges",
        headerName: "Exp. in Other Colleges",
        flex: 1.5,
        minWidth: 150,
        valueFormatter: ({ value }) =>
          value === null || value === undefined || typeof value === "object" ? "" : value,
      },
      { field: "gender", headerName: "Gender", flex: 0.8, minWidth: 80 },
      {
        field: "designation",
        headerName: "Designation",
        flex: 1.3,
        minWidth: 130,
        valueGetter: (params) => params.row.designation?.designation_name || "",
      },
      {
        field: "qualification",
        headerName: "Qualification",
        flex: 1.3,
        minWidth: 130,
        valueGetter: (params) => params.row.qualification?.qualification_name || "",
      },
      { field: "course_code", headerName: "Course Code", flex: 1.2, minWidth: 120 },
      { field: "staff_type", headerName: "Staff Type", flex: 1, minWidth: 100 },

      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        flex: 1,
        minWidth: 130,
        renderCell: (params) => (
          <ActionsMenu
            row={params.row}
            onEdit={() => handleOpenEdit(params.row)}
            onDelete={() => handleDelete(params.row.staff_id)}
            hasPermissionDirect={hasPermissionDirect}
          />
        ),
      },
    ],
    []
  );

  return (
    <Box p={2}>
      <Paper elevation={1}>
        <Toolbar sx={{ gap: 1, flexWrap: "wrap" }}>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            INTERNAL STAFF MANAGEMENT
          </Typography>
          <Button
            variant="contained"
            startIcon={<MoreVertIcon />}
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              "&:hover": { backgroundColor: "#115293" },
            }}
            onClick={handleMenuOpen}
          >
            ACTIONS
          </Button>
          <Menu anchorEl={anchorEl} open={openMenu} onClose={handleMenuClose}>
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                startIcon={<AddIcon />}
                sx={{
                  backgroundColor: "#1976d2",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#115293" },
                }}
                onClick={() => {
                  handleMenuClose();
                  handleOpenAdd();
                }}
              >
                ADD INTERNAL STAFF
              </Button>
            </MenuItem>
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                startIcon={<DownloadIcon />}
                sx={{
                  backgroundColor: "#4caf50",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#357a38" },
                }}
                onClick={() => {
                  handleMenuClose();
                  handleDownloadCsv();
                }}
              >
                DOWNLOAD CSV
              </Button>
            </MenuItem>
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                startIcon={<PictureAsPdfIcon />}
                sx={{
                  backgroundColor: "#e53935",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#c62828" },
                }}
                onClick={() => {
                  handleMenuClose();
                  handleDownloadPdf();
                }}
              >
                DOWNLOAD PDF
              </Button>
            </MenuItem>
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                component="label"
                startIcon={<UploadFileIcon />}
                sx={{
                  backgroundColor: "#ff9800",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#c77700" },
                }}
              >
                UPLOAD CSV
                <input
                  hidden
                  type="file"
                  accept=".csv"
                  onChange={(e) => {
                    handleMenuClose();
                    handleUploadCsv(e);
                  }}
                />
              </Button>
            </MenuItem>
          </Menu>
          <TextField
            size="medium"
            placeholder="Search data"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => fetchData()}>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Toolbar>

        <Box sx={{ height: 560, ml: 2, mr: 2 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            page={page}
            onPageChange={(newPage) => setPage(newPage)}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            pagination
            disableSelectionOnClick
            sx={{
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #e0e0e0",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f5f5f5",
                borderBottom: "2px solid #e0e0e0",
              },
            }}
          />
        </Box>
      </Paper>

      {/* Add/Edit Dialog */}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? "Edit Internal Staff" : "Add Internal Staff"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
              <TextField
                label="Staff Code"
                fullWidth
                value={staffFormData.staff_code}
                onChange={(e) => setStaffFormData({ ...staffFormData, staff_code: e.target.value })}
              />

              <TextField
                select
                label="Salutation"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={staffFormData.salutation}
                onChange={(e) => setStaffFormData({ ...staffFormData, salutation: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="">Select Salutation</option>
                <option value="Mr.">Mr.</option>
                <option value="Ms.">Ms.</option>
                <option value="Mrs.">Mrs.</option>
                <option value="Dr.">Dr.</option>
                <option value="Prof.">Prof.</option>
              </TextField>
            </Box>

            <TextField
              label="Staff Name"
              required
              fullWidth
              value={staffFormData.staff_name}
              onChange={(e) => setStaffFormData({ ...staffFormData, staff_name: e.target.value })}
              error={!!staffFormErrors.staff_name}
              helperText={staffFormErrors.staff_name}
            />

            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="First Name"
                fullWidth
                value={staffFormData.first_name}
                onChange={(e) => setStaffFormData({ ...staffFormData, first_name: e.target.value })}
              />
              <TextField
                label="Middle Name"
                fullWidth
                value={staffFormData.middle_name}
                onChange={(e) =>
                  setStaffFormData({ ...staffFormData, middle_name: e.target.value })
                }
              />
              <TextField
                label="Last Name"
                fullWidth
                value={staffFormData.last_name}
                onChange={(e) => setStaffFormData({ ...staffFormData, last_name: e.target.value })}
              />
            </Box>

            <TextField
              label="Father Name"
              fullWidth
              value={staffFormData.father_name}
              onChange={(e) => setStaffFormData({ ...staffFormData, father_name: e.target.value })}
            />

            <TextField
              label="Email"
              required
              type="email"
              fullWidth
              value={staffFormData.email}
              onChange={(e) => setStaffFormData({ ...staffFormData, email: e.target.value })}
              error={!!staffFormErrors.email}
              helperText={staffFormErrors.email}
            />

            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Aadhar Number"
                fullWidth
                value={staffFormData.aadhar_no}
                onChange={(e) => setStaffFormData({ ...staffFormData, aadhar_no: e.target.value })}
                error={!!staffFormErrors.aadhar_no}
                helperText={staffFormErrors.aadhar_no}
              />
              <TextField
                label="PAN Number"
                fullWidth
                value={staffFormData.pan_no}
                onChange={(e) => setStaffFormData({ ...staffFormData, pan_no: e.target.value })}
                error={!!staffFormErrors.pan_no}
                helperText={staffFormErrors.pan_no}
              />
            </Box>

            <TextField
              label="Mobile Number"
              fullWidth
              value={staffFormData.mobile}
              onChange={(e) => setStaffFormData({ ...staffFormData, mobile: e.target.value })}
              error={!!staffFormErrors.mobile}
              helperText={staffFormErrors.mobile}
            />

            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Experience in Our College (Years)"
                fullWidth
                type="number"
                inputProps={{ min: 0, max: 50, step: 0.5 }}
                value={staffFormData.experience_in_college}
                onChange={(e) =>
                  setStaffFormData((current) => ({
                    ...current,
                    experience_in_college: e.target.value,
                  }))
                }
                helperText="Enter experience in years (e.g., 2.5)"
              />
              <TextField
                label="Experience in Other Colleges (Years)"
                fullWidth
                type="number"
                inputProps={{ min: 0, max: 50, step: 0.5 }}
                value={staffFormData.experience_in_outer_colleges}
                onChange={(e) =>
                  setStaffFormData((current) => ({
                    ...current,
                    experience_in_outer_colleges: e.target.value,
                  }))
                }
                helperText="Enter experience in years (e.g., 3.0)"
              />
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Date of Birth"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={staffFormData.dob}
                onChange={(e) => setStaffFormData({ ...staffFormData, dob: e.target.value })}
              />
              <TextField
                label="Date of Joining"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={staffFormData.doj}
                onChange={(e) => setStaffFormData({ ...staffFormData, doj: e.target.value })}
              />
              <TextField
                label="Date of Resignation"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={staffFormData.dor}
                onChange={(e) => setStaffFormData({ ...staffFormData, dor: e.target.value })}
              />
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Autocomplete
                options={designations}
                getOptionLabel={(option) => option.designation_name || ""}
                value={
                  designations.find(
                    (designation) =>
                      String(designation.staff_designation_id) ===
                      String(staffFormData.designation_id)
                  ) || null
                }
                onChange={(event, newValue) => {
                  setStaffFormData({
                    ...staffFormData,
                    designation_id: newValue ? Number(newValue.staff_designation_id) : "",
                  });
                }}
                loading={designationsLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Designation"
                    fullWidth
                    helperText="Search and select a designation"
                  />
                )}
                isOptionEqualToValue={(option, value) =>
                  String(option.staff_designation_id) === String(value.staff_designation_id)
                }
                sx={{ flexGrow: 1 }}
              />

              <Autocomplete
                options={qualifications}
                getOptionLabel={(option) => option.qualification_name || ""}
                value={
                  qualifications.find(
                    (qualification) =>
                      String(qualification.staff_qualification_id) ===
                      String(staffFormData.qualification_id)
                  ) || null
                }
                onChange={(event, newValue) => {
                  setStaffFormData({
                    ...staffFormData,
                    qualification_id: newValue ? Number(newValue.staff_qualification_id) : "",
                  });
                }}
                loading={qualificationsLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Qualification"
                    fullWidth
                    helperText="Search and select a qualification"
                  />
                )}
                isOptionEqualToValue={(option, value) =>
                  String(option.staff_qualification_id) === String(value.staff_qualification_id)
                }
                sx={{ flexGrow: 1 }}
              />
            </Box>

            <TextField
              select
              label="Department"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={staffFormData.department_id}
              onChange={(event) => {
                if (event.target.value === "add-department") {
                  setDepartmentForm({ department_code: "", department_name: "" });
                  setDepartmentSaveError("");
                  setDepartmentDialogOpen(true);
                  return;
                }
                setStaffFormData({ ...staffFormData, department_id: event.target.value });
              }}
              disabled={departmentsLoading || Boolean(departmentsError)}
              error={Boolean(departmentsError)}
              helperText={
                departmentsError ||
                (departmentsLoading
                  ? "Loading departments..."
                  : departments.length === 0
                  ? "No active departments available for this college"
                  : "Select a department")
              }
            >
              <MenuItem value="">None</MenuItem>
              {staffFormData.department_id &&
                !departments.some(
                  (department) =>
                    String(department.department_id) === String(staffFormData.department_id)
                ) && (
                  <MenuItem value={staffFormData.department_id} disabled>
                    Department {staffFormData.department_id} (unavailable)
                  </MenuItem>
                )}
              {departments.map((department) => (
                <MenuItem key={department.department_id} value={department.department_id}>
                  {department.department_name}
                </MenuItem>
              ))}
              <MenuItem value="add-department" sx={{ color: "primary.main", fontWeight: 600 }}>
                <AddIcon fontSize="small" sx={{ mr: 1 }} /> Add Department
              </MenuItem>
            </TextField>

            <Autocomplete
              options={courseCodes}
              getOptionLabel={(option) => option.course_code || ""}
              value={
                courseCodes.find((course) => course.course_code === staffFormData.course_code) ||
                null
              }
              onChange={(event, newValue) => {
                setStaffFormData({
                  ...staffFormData,
                  course_code: newValue ? newValue.course_code : "",
                });
              }}
              loading={courseCodesLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Course Code"
                  fullWidth
                  helperText="Search and select a course code"
                />
              )}
              isOptionEqualToValue={(option, value) => option.course_code === value.course_code}
              filterOptions={(options, { inputValue }) =>
                options.filter(
                  (option) =>
                    String(option.course_code || "")
                      .toLowerCase()
                      .includes(inputValue.toLowerCase()) ||
                    (option.course_name &&
                      option.course_name.toLowerCase().includes(inputValue.toLowerCase()))
                )
              }
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      {option.course_code}
                    </Typography>
                    {option.course_name && (
                      <Typography variant="body2" color="text.secondary">
                        {option.course_name}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            />

            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                select
                margin="normal"
                label="Gender"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={staffFormData.gender}
                onChange={(e) => setStaffFormData({ ...staffFormData, gender: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="">Select Gender</option>
                <option value="M">Male</option>
                <option value="F">Female</option>
                <option value="O">Other</option>
              </TextField>

              <TextField
                select
                margin="normal"
                label="Staff Type"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={staffFormData.staff_type}
                onChange={(e) => setStaffFormData({ ...staffFormData, staff_type: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="">Select Staff Type</option>
                <option value="T">Teaching</option>
                <option value="NT">Non-Teaching</option>
                <option value="A">Administrative</option>
                <option value="C">Contract</option>
              </TextField>
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                select
                margin="normal"
                label="Staff Status"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={staffFormData.staff_status}
                onChange={(e) =>
                  setStaffFormData({ ...staffFormData, staff_status: e.target.value })
                }
                SelectProps={{ native: true }}
              >
                <option value="">Select Staff Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </TextField>

              <TextField
                select
                margin="normal"
                label="Workflow Status"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={staffFormData.workflow_status}
                onChange={(e) =>
                  setStaffFormData({ ...staffFormData, workflow_status: e.target.value })
                }
                SelectProps={{ native: true }}
              >
                <option value="">Select Workflow Status</option>
                <option value="DRAFT">Draft</option>
                <option value="APPROVED">Approved</option>
                <option value="INACTIVE">Inactive</option>
              </TextField>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{
              ml: 2,
              backgroundColor: "#ff9800",
              color: "#fff",
              "&:hover": { backgroundColor: "#c77700" },
            }}
          >
            {editing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={departmentDialogOpen}
        onClose={() => {
          if (!departmentSaving) setDepartmentDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Department</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Department Code"
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ maxLength: 20 }}
              value={departmentForm.department_code}
              disabled={departmentSaving}
              onChange={(event) =>
                setDepartmentForm((current) => ({
                  ...current,
                  department_code: event.target.value,
                }))
              }
            />
            <TextField
              label="Department Name"
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ maxLength: 50 }}
              value={departmentForm.department_name}
              disabled={departmentSaving}
              onChange={(event) =>
                setDepartmentForm((current) => ({
                  ...current,
                  department_name: event.target.value,
                }))
              }
            />
            {departmentSaveError && <MuiAlert severity="error">{departmentSaveError}</MuiAlert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={departmentSaving} onClick={() => setDepartmentDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            disabled={departmentSaving}
            onClick={handleAddDepartment}
            variant="contained"
            sx={{ color: "#fff" }}
          >
            {departmentSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
